import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sun, LayoutGrid, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeToggle } from './ThemeToggle';
import { ChangePasswordButton } from './ChangePasswordDialog';
import { GlowBackground } from './GlowBackground';
import { HSMark } from './TrainerShell';

const TABS = [
  { key: 'today', label: 'Dziś', to: '/client-dashboard', icon: Sun },
  { key: 'panel', label: 'Panel', to: '/client-panel', icon: LayoutGrid },
];
// Plan, journal and weekly reports open from "Panel", so that tab stays lit there.
const isActive = (to, pathname) => pathname === to || (to === '/client-panel' && pathname.startsWith('/client-plan'));

const iconBtn = 'w-11 h-11 rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors';

// Client layout (style B): only two places — "Dziś" (check-in + today's workout) and "Panel" (plan, payment, history).
// On phones it looks like a locked app window (.client-frame): header and tab bar stick to the screen edges.
// `action` = the screen's main button, pinned above the tab bar on phones.
export const ClientShell = ({ children, action }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="client-frame min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)] relative">
      <GlowBackground />

      <header className="client-frame-top app-content flex items-center justify-between gap-4 px-4 sm:px-10 pb-2 sm:pb-0">
        <div className="flex items-center gap-3">
          <HSMark size={42} />
          <span className="hidden sm:inline text-sm text-[var(--text-muted)] tracking-wide">hstrener.com</span>
        </div>
        <nav className="hidden sm:flex glass rounded-full p-1.5 gap-1" aria-label="Menu klienta">
          {TABS.map(({ key, label, to }) => (
            <button key={key} onClick={() => navigate(to)}
              className={`h-11 px-7 rounded-full text-xs font-semibold tracking-[0.2em] uppercase text-[var(--text-muted)] ${isActive(to, pathname) ? 'nav-active' : ''}`}
              data-testid={`client-tab-${key}`}>
              {label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-1">
          <ThemeToggle className="w-11 h-11" />
          <ChangePasswordButton className={iconBtn} />
          <button onClick={handleLogout} aria-label="Wyloguj" title="Wyloguj" className={iconBtn} data-testid="logout-button">
            <LogOut className="w-5 h-5" strokeWidth={1.6} />
          </button>
        </div>
      </header>

      <div className="client-frame-scroll app-content pb-6 sm:pb-16">{children}</div>

      <div className="client-frame-bottom sm:hidden app-content px-4 pt-3 flex flex-col gap-3">
        {action && <div data-testid="client-pinned-action">{action}</div>}
        <nav className="glass glass-float rounded-full p-1.5 flex gap-1" aria-label="Menu klienta">
          {TABS.map(({ key, label, to, icon: Icon }) => (
            <button key={key} onClick={() => navigate(to)}
              className={`flex-1 h-14 rounded-full flex items-center justify-center gap-2.5 text-xs font-semibold tracking-[0.2em] uppercase text-[var(--text-muted)] ${isActive(to, pathname) ? 'nav-active' : ''}`}
              data-testid={`client-tab-mobile-${key}`}>
              <Icon className="w-[17px] h-[17px]" strokeWidth={1.7} />{label}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
};
